import api from "./api";
import { normalizeApiEnvelope } from "@/utils/apiEnvelope";

/**
 * Service for Internal Purchase Orders (Requisitions)
 */

const BASE_URL = "/internal-orders";

const unwrap = (response) => normalizeApiEnvelope(response);

/**
 * List internal orders with filters
 */
export const listOrders = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append("status", filters.status);
  if (filters.providerId) params.append("providerId", filters.providerId);
  if (filters.createdBy) params.append("createdBy", filters.createdBy);
  if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.append("dateTo", filters.dateTo);
  if (filters.limit) params.append("limit", filters.limit);
  if (filters.offset) params.append("offset", filters.offset);

  const response = await api.get(`${BASE_URL}?${params.toString()}`);
  return unwrap(response);
};

/**
 * Get dashboard data for purchasing department
 */
export const getDashboard = async () => {
  const response = await api.get(`${BASE_URL}/dashboard`);
  return unwrap(response);
};

/**
 * Get orders grouped by supplier
 */
export const getOrdersBySupplier = async (status = "pending") => {
  const response = await api.get(`${BASE_URL}/by-supplier?status=${status}`);
  return unwrap(response);
};

/**
 * Get single order by ID
 */
export const getOrder = async (id) => {
  const response = await api.get(`${BASE_URL}/${id}`);
  return unwrap(response);
};

/**
 * Create new internal order
 */
export const createOrder = async (orderData, idempotencyKey) => {
  const headers = idempotencyKey
    ? { "Idempotency-Key": idempotencyKey }
    : undefined;
  const response = await api.post(BASE_URL, orderData, { headers });
  return unwrap(response);
};

/**
 * Update existing order
 */
export const updateOrder = async (id, orderData, idempotencyKey) => {
  const headers = idempotencyKey
    ? { "Idempotency-Key": idempotencyKey }
    : undefined;
  const response = await api.patch(`${BASE_URL}/${id}`, orderData, { headers });
  return unwrap(response);
};

/**
 * Delete order
 */
export const deleteOrder = async (id, idempotencyKey) => {
  const headers = idempotencyKey
    ? { "Idempotency-Key": idempotencyKey }
    : undefined;
  const response = await api.delete(`${BASE_URL}/${id}`, { headers });
  return unwrap(response);
};

/**
 * Convert internal order to formal Purchase Order
 */
export const convertToPurchase = async (
  id,
  { warehouseId, notes },
  idempotencyKey
) => {
  const headers = idempotencyKey
    ? { "Idempotency-Key": idempotencyKey }
    : undefined;
  const response = await api.post(
    `${BASE_URL}/${id}/convert`,
    { warehouseId, notes },
    { headers }
  );
  return unwrap(response);
};

export const approveOrder = async (
  id,
  { warehouseId, notes },
  idempotencyKey
) => {
  const headers = idempotencyKey
    ? { "Idempotency-Key": idempotencyKey }
    : undefined;
  const response = await api.post(
    `${BASE_URL}/${id}/approve`,
    { warehouseId, notes },
    { headers }
  );
  return unwrap(response);
};

/**
 * Cancel internal order
 */
export const cancelOrder = async (id, idempotencyKey) => {
  const headers = idempotencyKey
    ? { "Idempotency-Key": idempotencyKey }
    : undefined;
  const response = await api.post(
    `${BASE_URL}/${id}/cancel`,
    {},
    { headers }
  );
  return unwrap(response);
};

/**
 * Add item to existing order
 */
export const addItem = async (orderId, item, idempotencyKey) => {
  const headers = idempotencyKey
    ? { "Idempotency-Key": idempotencyKey }
    : undefined;
  const response = await api.post(
    `${BASE_URL}/${orderId}/items`,
    item,
    { headers }
  );
  return unwrap(response);
};

/**
 * Update item
 */
export const updateItem = async (orderId, itemId, updates, idempotencyKey) => {
  const headers = idempotencyKey
    ? { "Idempotency-Key": idempotencyKey }
    : undefined;
  const response = await api.patch(
    `${BASE_URL}/${orderId}/items/${itemId}`,
    updates,
    { headers }
  );
  return unwrap(response);
};

/**
 * Remove item from order
 */
export const removeItem = async (orderId, itemId, idempotencyKey) => {
  const headers = idempotencyKey
    ? { "Idempotency-Key": idempotencyKey }
    : undefined;
  const response = await api.delete(
    `${BASE_URL}/${orderId}/items/${itemId}`,
    { headers }
  );
  return unwrap(response);
};

/**
 * Generate unique idempotency key
 */
export const generateIdempotencyKey = (prefix = "iop") => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export default {
  listOrders,
  getDashboard,
  getOrdersBySupplier,
  getOrder,
  createOrder,
  updateOrder,
  deleteOrder,
  convertToPurchase,
  approveOrder,
  cancelOrder,
  addItem,
  updateItem,
  removeItem,
  generateIdempotencyKey,
};
